import React, { useCallback, useMemo } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useExpandCollapseAllState } from '../../hooks/useExpandCollapseAllState';
import { ReactComponent as CollapseAll } from '../Icons/unfold_less.svg';
import { ReactComponent as ExpandAll } from '../Icons/unfold_more.svg';

const useStyles = makeStyles((theme) => ({
  expandCollapse: {
    margin: theme.spacing(0, 0.5),
    padding: theme.spacing(0, 0.5)
  }
}));

export const useExpandCollapse = ({
  id,
  onExpandCollapseAll
}: ExpandCollapseAllButtonProps): {
  expandAllState: boolean;
  onCollapseExpandClick: () => void;
} => {
  const { expandAllState, setExpandCollapseState } = useExpandCollapseAllState(
    id
  );

  const onCollapseExpandClick = useCallback(() => {
    setExpandCollapseState(!expandAllState);

    if (onExpandCollapseAll) {
      onExpandCollapseAll(!expandAllState);
    }
  }, [expandAllState, setExpandCollapseState, onExpandCollapseAll]);

  return useMemo(
    () => ({
      expandAllState: Boolean(expandAllState),
      onCollapseExpandClick
    }),
    [expandAllState, onCollapseExpandClick]
  );
};

type ExpandCollapseAllButtonProps = {
  id: string;
  onExpandCollapseAll?: (state: boolean) => void;
};

export const ExpandCollapseAllButton: React.FC<ExpandCollapseAllButtonProps> = ({
  id,
  onExpandCollapseAll
}: ExpandCollapseAllButtonProps) => {
  const classes = useStyles();
  const { expandAllState, onCollapseExpandClick } = useExpandCollapse({
    id,
    onExpandCollapseAll
  });
  const icon = useMemo(
    () =>
      expandAllState ? (
        <CollapseAll data-testid="collapse-all" />
      ) : (
        <ExpandAll data-testid="expand-all" />
      ),
    [expandAllState]
  );

  const tooltipTitle = useMemo(
    () =>
      expandAllState ? (
        <FormattedMessage id="collapseAll" />
      ) : (
        <FormattedMessage id="expandAll" />
      ),
    [expandAllState]
  );

  return (
    <Tooltip title={tooltipTitle}>
      <IconButton
        data-testid="expand-collpase-all-btn"
        className={classes.expandCollapse}
        onClick={onCollapseExpandClick}
        size="large"
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
};
